import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "app/store";
import styles from "./SubscriptionPayment.module.scss";
import SubscriptionDetails from "components/billing/SubscriptionDetails";
import Preloader from "components/shared/Preloader";
import PageTabs from "components/shared/PageTabs";
import { IPageTab } from "components/shared/PageTabs/PageTabs";
import { customerInfoSelector, isLoadingCustomerInfoSelector } from "features/billing/customer/customerSlice";
import { isLoadingSubscriptionInfoSelector, subscriptionInfoSelector } from "features/billing/subscription/subscriptionSlice";
import { selectInfoUserSelector } from "features/user/userSlice";
import { isLoadingPricesSelector, pricesSelector } from "features/billing/prices/pricesSlice";
import { addSubscription, getSubscriptionInfoByOrganozationId } from "features/billing/subscription/subscriptionActions";
import { getCustomerByOrganizationId, updateCustomer } from "features/billing/customer/customerActions";
import { getPrices } from "features/billing/prices/pricesActions";
import { CustomerBillingInfoFormHandle } from "components/billing/CustomerBillingForm/CustomerBillingInfoForm";
import { PaymentFormHandle } from "components/billing/PaymentForm/PaymentForm";
import { CardInfoContext } from "./contexts/CardInfoContext";
import { CustomerInfoContext } from "./contexts/CustomerInfoContext";
import { toast } from "react-toastify";
import { debounce } from 'lodash';
import { IoChevronBack } from "react-icons/io5";
import { useStripe } from "@stripe/react-stripe-js";
import { payLatestInvoiceBySubscriptionId } from "features/billing/invoiceInfo/invoiceInfoActions";
import { InvoiceStatus } from "features/billing/invoices/invoicesTypes";
import { getCouponById } from "features/billing/couponInfo/couponInfoActions";
import { billingFormsCustomerInfoSelector, billingFormsSubscriptionInfoSelector } from "features/billing/formStates/billingFormsInfo/billingFormsInfoSlice";
import { ISubscription, SubscriptionStatus } from "features/billing/subscription/subscriptionTypes";
import { getUpcomingInvoice, getUpcomingInvoiceForSubscription } from "features/billing/upcomingInvoice/upcomingInvoiceActions";
import { setCustomerFormInfoTaxIdErrors, setSubscriptionFormInfo } from "features/billing/formStates/billingFormsInfo/billingFormsInfoActions";
import { CustomerFormInfo, SubscriptionFormInfo } from "features/billing/formStates/billingFormsInfo/billingFormsInfoTypes";
import { getCurrencies } from "features/currencies/currenciesActions";
import { getMembers } from "features/members/membersActions";

const SubscriptionPayment: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();

  const [cardInfoFormRef, setCardInfoFormRef] = useState<React.RefObject<PaymentFormHandle> | null>(null);
  const [customerInfoFormRef, setCustomerInfoFormRef] = useState<React.RefObject<CustomerBillingInfoFormHandle> | null>(null);

  const customerFormInfo = useSelector(billingFormsCustomerInfoSelector);
  const subscriptionFormInfo = useSelector(billingFormsSubscriptionInfoSelector);

  const userInfo = useSelector(selectInfoUserSelector);
  const subscriptionInfo = useSelector(subscriptionInfoSelector);
  const customerInfo = useSelector(customerInfoSelector);
  const prices = useSelector(pricesSelector);

  const subscriptionIsLoading = useSelector(isLoadingSubscriptionInfoSelector);
  const customerIsLoading = useSelector(isLoadingCustomerInfoSelector);
  const pricesAreLoading = useSelector(isLoadingPricesSelector);

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const tabsData: IPageTab[] = [
    {
      path: `/payment`,
      name: 'Customer info',
    },
    {
      path: `/payment/card`,
      name: 'Card info',
      onClick: (event) => {
        event.preventDefault();

        const customerFormIsInvalid =
          (customerInfoFormRef && !(customerInfoFormRef?.current?.triggerSubmit())) ||
          Object.values(customerFormInfo!.addressErrors).every(value => value.trim() !== '');

        if (customerFormIsInvalid) {
          toast.error('Please fill in valid values in the Customer Info form');
          return;
        }

        dispatch(updateCustomer({
          customerId: customerInfo.id,
          address: customerFormInfo!.address,
          enableAutomaticTax: true,
          taxIds: Object.entries(customerFormInfo!.taxIds).map(([type, id]) => ({ type, id })),
        }))
          .then(result => {
            if (result.meta.requestStatus === 'rejected') {
              toast.error('Please fill in valid value for TAX ID');
              dispatch(setCustomerFormInfoTaxIdErrors(
                Object.keys(customerFormInfo!.taxIdErrors).reduce((acc, key) => {
                  acc[key] = 'TAX ID is invalid';
                  return acc;
                }, { ...customerFormInfo!.taxIdErrors })
              ));
            }
            else {
              dispatch(setCustomerFormInfoTaxIdErrors(
                Object.keys(customerFormInfo!.taxIdErrors).reduce((acc, key) => {
                  acc[key] = '';
                  return acc;
                }, { ...customerFormInfo!.taxIdErrors })
              ));
              navigate("/payment/card")
            }
          });
      }
    }
  ]

  useEffect(() => {
    if (userInfo?.organizationId) {
      dispatch(getMembers())
      dispatch(getSubscriptionInfoByOrganozationId(userInfo.organizationId));
      dispatch(getCurrencies())
      dispatch(getCustomerByOrganizationId(userInfo.organizationId));
      dispatch(getPrices());
    }
  }, [userInfo]);

  useEffect(() => {
    if (!subscriptionInfo) return;

    dispatch(setSubscriptionFormInfo({
      ...subscriptionFormInfo,
      productId: subscriptionInfo.productId,
      interval: subscriptionInfo.interval,
      currency: subscriptionInfo.currency,
      quantity: subscriptionInfo.quantity,
    }));

    if (subscriptionInfo?.couponId) {
      dispatch(getCouponById(subscriptionInfo.couponId))
    }
  }, [subscriptionInfo]);

  const debouncedUpcomingInvoiceFetch = useMemo(
    () => debounce((customerFormInfo: CustomerFormInfo, subscriptionFormInfo: SubscriptionFormInfo, subscriptionInfo: ISubscription | null) => {
      if (!subscriptionInfo) return;

      const interval = subscriptionFormInfo?.interval ?? subscriptionInfo!.interval;
      const currency = subscriptionFormInfo?.currency ?? subscriptionInfo!.currency;
      const productId = subscriptionFormInfo?.productId ?? subscriptionInfo!.productId;
      const quantity = subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity;
      const couponId = subscriptionFormInfo?.couponId ?? subscriptionInfo!.couponId;
      const priceId = prices.find((price) =>
        price.interval === interval &&
        price.productId === productId &&
        price.currency === currency
      )?.id ?? subscriptionInfo!.priceId

      const subscriptionIsActive = subscriptionInfo?.status === SubscriptionStatus.Active;
      const subscriptionIsTrialing = subscriptionInfo?.status === SubscriptionStatus.Trialing;
      const isActiveOrTrialing = subscriptionIsActive || subscriptionIsTrialing;

      if (!isActiveOrTrialing) {
        dispatch(getUpcomingInvoice({
          quantity: quantity,
          priceId: priceId,
          couponId: couponId,
          address: customerFormInfo.address,
          taxIds: Object.entries(customerFormInfo.taxIds).map(([type, id]) => ({ type, id })),
        }));
      }
      else {
        if (interval !== subscriptionInfo!.interval || quantity !== subscriptionInfo!.quantity) {
          dispatch(getUpcomingInvoiceForSubscription({
            subscriptionId: subscriptionInfo!.id,
            quantity: quantity,
            priceId: priceId,
          }));
        }
      }
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    debouncedUpcomingInvoiceFetch(customerFormInfo, subscriptionFormInfo, subscriptionInfo);
    return () => debouncedUpcomingInvoiceFetch.cancel();
  }, [customerFormInfo, subscriptionFormInfo, debouncedUpcomingInvoiceFetch]);

  useEffect(() => {
    debouncedUpcomingInvoiceFetch(customerFormInfo, subscriptionFormInfo, subscriptionInfo);
    return () => debouncedUpcomingInvoiceFetch.cancel();
  }, [customerFormInfo, subscriptionFormInfo, debouncedUpcomingInvoiceFetch]);

  const handleSubscriptionPayment = async (quantity: number, priceId: string, couponId: string | null) => {
    const customerFormIsInvalid =
      (customerInfoFormRef && !(customerInfoFormRef?.current?.triggerSubmit())) ||
      Object.values(customerFormInfo!.addressErrors).every(value => value.trim() !== '')

    if (customerFormIsInvalid) {
      toast.error('To complete your subscription payment, please fill in all required payment details.')
      return;
    }

    const complited = await cardInfoFormRef?.current?.triggerSubmit();
    if (!complited?.success) {
      toast.error('To complete your subscription payment, please fill in all required payment details.')
      return;
    }

    setIsProcessingPayment(true)

    try {
      await dispatch(updateCustomer({
        customerId: customerInfo.id,
        address: customerFormInfo!.address,
        enableAutomaticTax: true,
        taxIds: Object.entries(customerFormInfo!.taxIds).map(([type, id]) => ({ type, id })),
      }));

      const newSubscription = await dispatch(addSubscription({
        organizationId: userInfo?.organizationId as number,
        model: {
          subscriptionId: subscriptionInfo!.id,
          priceId: priceId ?? subscriptionInfo!.priceId,
          quantity: quantity,
          couponId: couponId
        }
      })).unwrap();

      try {
        const invoice = await dispatch(payLatestInvoiceBySubscriptionId(newSubscription!.id)).unwrap();

        if (invoice.status === InvoiceStatus.Paid) {
          navigate('/subscription');
          return;
        }

        if (!stripe) {
          console.error("Stripe instance not found.");
          return;
        }
        if (!invoice.confirmationClientSecret) {
          console.error("Client secret not found.");
          return;
        }

        const { error } = await stripe.confirmCardPayment(invoice.confirmationClientSecret);

        if (error) {
          toast.error("Payment failed")
          return false;
        }
      } catch (error) {
        toast.error("Payment failed")
        return false;
      }
    } catch (error) {
      console.error("An error occurred during the process:", error);
      return;
    }
    finally {
      setIsProcessingPayment(false);
    }

    navigate('/subscription');
  };

  if (!subscriptionInfo || subscriptionIsLoading || customerIsLoading || pricesAreLoading || isProcessingPayment) {
    return <Preloader />
  }

  return (
    <CardInfoContext.Provider value={{ cardInfoFormRef, setCardInfoFormRef }}>
      <CustomerInfoContext.Provider value={{ customerInfoFormRef, setCustomerInfoFormRef }}>
        <div className={styles.tabsAndBack}>
          <span className={styles.backButton} onClick={() => navigate('/subscription/plans')}>
            <IoChevronBack size="16px" />
          </span>
          <PageTabs tabsData={tabsData} />
        </div>
        <div className={styles.container}>
          <div className={styles.billingInfo}>
            <Outlet />
          </div>
          <div>
            <SubscriptionDetails
              intervalSelectionEnabled={true}
              submitButtonHidden={location.pathname !== `/payment/card`}
              handleSubmit={async (quantity, priceId, couponId) => {
                await handleSubscriptionPayment(quantity, priceId, couponId);
              }}
            />
          </div>
        </div>
      </CustomerInfoContext.Provider>
    </CardInfoContext.Provider>
  )
};

export default SubscriptionPayment;
